import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Activity, Layers, Crosshair, TrendingUp, Gauge, AlertTriangle, Target, Split,
  Zap, ShieldAlert, Brain, Loader2, Cpu, WifiOff, HelpCircle, Check, X, Search,
  Lock, ArrowRight, MessageSquareWarning,
} from "lucide-react";
import api, { API, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { UpgradePrompt, aiLimitDetail, notifyAiLimit } from "@/components/AiLimit";
import HelpPopover from "@/components/HelpPopover";

const ACTION_STYLE = {
  ACTIONABLE: "text-tc-bull border-tc-bull/40 bg-tc-bull/10",
  CONDITIONAL: "text-tc-coach border-tc-coach/40 bg-tc-coach/10",
  WAIT: "text-tc-xp border-tc-xp/40 bg-tc-xp/10",
  PASS: "text-tc-muted border-white/15 bg-white/5",
  INSUFFICIENT_EVIDENCE: "text-tc-muted border-white/15 bg-white/5",
};
const KIND_STYLE = {
  FACT: "border-tc-coach/50 text-tc-sub",
  CALCULATION: "border-tc-mastery/50 text-tc-sub",
  INTERPRETATION: "border-tc-xp/50 text-tc-sub",
  CONFLICT: "border-tc-bear/60 text-tc-bear",
  CONDITION: "border-tc-bull/50 text-tc-sub",
  UNKNOWN: "border-white/20 text-tc-muted",
};

export default function Analyst() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [authz, setAuthz] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioId, setScenarioId] = useState(params.get("scenario") || "");
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [scenarioMeta, setScenarioMeta] = useState(null);
  const [detMs, setDetMs] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  const [interp, setInterp] = useState(null);
  const [explainOpen, setExplainOpen] = useState(null);
  const [explainText, setExplainText] = useState("");
  const draftRef = useRef(null);

  useEffect(() => {
    api.get("/analyst/authorization").then((r) => setAuthz(r.data)).catch(() => setAuthz({ authorization: { authorized: false, reason: "Unable to check authorization." } }));
    api.get("/analyst/scenarios").then((r) => setScenarios(r.data.scenarios || [])).catch(() => {});
    const preAid = params.get("assessment");
    if (preAid) loadAssessment(preAid);
  }, []);

  const loadAssessment = async (aid) => {
    setLoading(true); setAssessment(null); setInterp(null); setDraft(""); setStreaming(false);
    try {
      const { data } = await api.get(`/analyst/assessment/${aid}`);
      setAssessment(data.assessment); setAssessmentId(aid); setScenarioMeta(data.scenario); setDetMs(0);
      setLoading(false);
      streamInterpretation(aid);
    } catch (e) { setLoading(false); toast.error(apiErr(e)); }
  };

  useEffect(() => { if (draftRef.current) draftRef.current.scrollTop = draftRef.current.scrollHeight; }, [draft]);

  const runAnalysis = async () => {
    setLoading(true); setAssessment(null); setInterp(null); setDraft(""); setStreaming(false);
    try {
      const { data } = await api.post("/analyst/analyze", scenarioId ? { scenario: scenarioId } : {});
      setAssessment(data.assessment);
      setAssessmentId(data.assessment_id);
      setScenarioMeta(data.scenario);
      setDetMs(data.telemetry?.time_to_deterministic_ms);
      setLoading(false);
      streamInterpretation(data.assessment_id);
    } catch (e) {
      setLoading(false);
      toast.error(apiErr(e));
    }
  };

  const streamInterpretation = async (aid) => {
    setStreaming(true); setDraft(""); setInterp(null);
    try {
      const token = localStorage.getItem("tc_token");
      const resp = await fetch(`${API}/analyst/interpret/stream?assessment_id=${aid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const o = JSON.parse(line.slice(5).trim());
          if (o.type === "delta") setDraft((d) => d + o.content);
          else if (o.type === "interpretation") setInterp(o.data);
          else if (o.type === "limit_reached") setInterp({ available: false, reason: o.message, tier: o.tier, limit_reached: true });
        }
      }
    } catch (e) {
      setInterp({ available: false, reason: "stream_failed" });
    } finally {
      setStreaming(false);
    }
  };

  const openExplain = async (topic) => {
    setExplainOpen(topic); setExplainText("");
    try {
      const { data } = await api.post("/analyst/explain", { assessment_id: assessmentId, topic });
      setExplainText(data.available ? data.text : "Explanation temporarily unavailable.");
    } catch (e) { const d = aiLimitDetail(e); if (d) notifyAiLimit(d, navigate); setExplainText(d ? d.message : "Explanation temporarily unavailable."); }
  };

  if (authz && !authz.authorization.authorized) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="tc-card p-8 text-center border-tc-mastery/25" data-testid="analyst-gated">
          <Lock className="w-8 h-8 mx-auto text-tc-mastery" />
          <h1 className="font-heading text-2xl font-bold mt-3">ANALYST is not unlocked yet</h1>
          <p className="text-tc-sub mt-2">{authz.authorization.reason}</p>
          <p className="text-tc-muted text-sm mt-2">{authz.is_minor ? "Demonstrate competency and ask your guardian to authorize ANALYST." : ""}</p>
          {authz.authorization.plan_required
            ? <button data-testid="analyst-see-plans-btn" onClick={() => navigate("/pricing")} className="mt-5 rounded-lg bg-tc-coach text-tc-bg font-semibold px-5 py-2.5">See plans</button>
            : <button data-testid="analyst-back-btn" onClick={() => navigate("/dashboard")} className="mt-5 rounded-lg bg-tc-coach text-tc-bg font-semibold px-5 py-2.5">Back to Dashboard</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-tc-coach text-xs font-mono uppercase tracking-widest"><Search className="w-3.5 h-3.5" /> ANALYZE FOR ME · TRADECRAFT ANALYST</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Market Intelligence</h1>
          <p className="text-tc-sub mt-1 max-w-xl text-sm">A conditional, evidence-first read of the market at time T. Deterministic math is authoritative; the AI interprets — you decide.</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpPopover testid="analyst-help"
            what="A professional market read at time T. TRADECRAFT computes the objective evidence — regime, structure, levels, risk geometry — and Claude interprets it. You keep the final judgment."
            why="It reasons in conditions and probabilities, not certainty. Often the best answer is WAIT or PASS — that's a feature, not a failure."
            how="Pick a market and press Analyze. The deterministic read appears instantly; the AI interpretation streams in. Tap 'explain' on anything you don't understand, and challenge the analysis at the bottom." />
          <select data-testid="analyst-scenario-select" value={scenarioId} onChange={(e) => setScenarioId(e.target.value)}
            className="bg-tc-panel border border-white/10 rounded-lg px-3 py-2 text-sm text-tc-sub max-w-[200px]">
            <option value="">Featured market</option>
            {scenarios.map((s) => <option key={s.id} value={s.id}>{s.symbol} · {s.name || s.id}</option>)}
          </select>
          <button data-testid="analyst-run-btn" onClick={runAnalysis} disabled={loading}
            className="rounded-lg bg-tc-coach text-tc-bg font-bold px-5 py-2.5 hover:bg-tc-coach/90 transition-colors flex items-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</> : <>Analyze <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>

      {!assessment && !loading && (
        <div className="tc-card p-10 text-center text-tc-muted" data-testid="analyst-empty">
          <Activity className="w-8 h-8 mx-auto opacity-40" />
          <p className="mt-3">Pick a market and run ANALYST. The deterministic read appears instantly; the AI interpretation streams in.</p>
        </div>
      )}

      {assessment && (
        <>
          <ActionBanner a={assessment} meta={scenarioMeta} detMs={detMs} />

          <SectionLabel icon={Cpu} tone="text-tc-coach" title="Deterministic Evidence" sub="What TRADECRAFT can calculate / observe · math is authoritative" testid="det-label" />

          <div className="grid lg:grid-cols-2 gap-4">
            <RegimeCard a={assessment} onExplain={openExplain} />
            <HtfCard a={assessment} />
            <LevelsCard a={assessment} onExplain={openExplain} />
            <RiskGeoCard a={assessment} onExplain={openExplain} />
          </div>

          <EvidenceCard a={assessment} onExplain={openExplain} />

          <div className="grid lg:grid-cols-2 gap-4">
            <ThesisCard a={assessment} />
            <ConditionsCard a={assessment} />
          </div>

          {/* ---------- AI INTERPRETATION (streamed) ---------- */}
          <SectionLabel icon={Brain} tone="text-tc-mastery" title="AI Interpretation" sub="How TRADECRAFT's AI reasons about that evidence · a judgment, not a fact" testid="ai-label" />
          <InterpretationCard streaming={streaming} draft={draft} interp={interp} draftRef={draftRef} onRetry={() => streamInterpretation(assessmentId)} />

          <ChallengeBar assessmentId={assessmentId} />
        </>
      )}

      {explainOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setExplainOpen(null)} data-testid="explain-modal">
          <div className="tc-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><HelpCircle className="w-4 h-4" /> Explain</div>
              <button onClick={() => setExplainOpen(null)} className="text-tc-muted hover:text-tc-txt"><X className="w-4 h-4" /></button>
            </div>
            <h3 className="font-heading font-semibold mt-2">{explainOpen}</h3>
            <div className="mt-3 text-sm text-tc-sub min-h-[60px]" data-testid="explain-text">
              {explainText || <span className="flex items-center gap-2 text-tc-muted"><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- sub-components ---------------- */
function SectionLabel({ icon: Icon, tone, title, sub, testid }) {
  return (
    <div className="flex items-center gap-2 pt-1" data-testid={testid}>
      <Icon className={`w-4 h-4 ${tone}`} />
      <div>
        <div className={`text-[11px] font-mono uppercase tracking-widest ${tone}`}>{title}</div>
        <div className="text-[10px] font-mono text-tc-muted">{sub}</div>
      </div>
    </div>
  );
}

function ActionBanner({ a, meta, detMs }) {
  const style = ACTION_STYLE[a.actionability] || ACTION_STYLE.WAIT;
  return (
    <div className={`tc-card p-5 border ${style}`} data-testid="analyst-actionability">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">{meta?.symbol} · {meta?.interval} · current actionability</div>
          <div className="font-heading text-3xl font-bold mt-1">{a.actionability.replace(/_/g, " ")}</div>
        </div>
        <div className="text-right text-xs font-mono text-tc-muted">
          <div>Regime: <span className="text-tc-txt">{a.market_regime.regime.replace(/_/g, " ")}</span></div>
          <div>Lean: <span className="text-tc-txt">{a.lean}</span></div>
          {detMs != null && <div className="text-tc-bull">deterministic in {detMs}ms</div>}
        </div>
      </div>
      <p className="text-sm text-tc-sub mt-2">{a.primary_thesis?.risk_considerations}</p>
    </div>
  );
}

function ExplainChip({ topic, onExplain }) {
  return (
    <button data-testid={`explain-${topic.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => onExplain(topic)}
      className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-tc-muted hover:text-tc-coach hover:border-tc-coach/30 transition-colors inline-flex items-center gap-1">
      <HelpCircle className="w-2.5 h-2.5" /> explain
    </button>
  );
}

function Card({ icon: Icon, title, tone, children, chip }) {
  return (
    <div className="tc-card p-5 space-y-2">
      <div className="flex items-center justify-between">
        <div className={`text-[11px] font-mono uppercase tracking-widest ${tone} flex items-center gap-1.5`}><Icon className="w-3.5 h-3.5" /> {title}</div>
        {chip}
      </div>
      {children}
    </div>
  );
}

function RegimeCard({ a, onExplain }) {
  const r = a.market_regime;
  return (
    <Card icon={Activity} title="Market Regime" tone="text-tc-coach" chip={<ExplainChip topic={`why is this a ${r.regime.replace(/_/g, " ")} regime?`} onExplain={onExplain} />}>
      <div className="text-lg font-heading font-semibold" data-testid="analyst-regime">{r.regime.replace(/_/g, " ")}<span className="text-xs font-mono text-tc-muted ml-2">strength {r.regime_strength}</span></div>
      {r.regime_evidence.map((e, i) => <div key={i} className="text-[12px] text-tc-sub">• {e}</div>)}
      {r.regime_conflicts.map((e, i) => <div key={i} className="text-[12px] text-tc-bear">⚠ {e}</div>)}
    </Card>
  );
}

function HtfCard({ a }) {
  const h = a.higher_timeframe_context;
  return (
    <Card icon={Layers} title="Higher-Timeframe Context" tone="text-tc-mastery">
      <div className="flex gap-2 flex-wrap">
        {["tf_4h", "tf_1h", "tf_15m"].map((k) => (
          <span key={k} className="text-[11px] font-mono px-2 py-1 rounded bg-white/5 border border-white/10">{k.replace("tf_", "").toUpperCase()}: <span className="text-tc-txt">{h[k]}</span></span>
        ))}
      </div>
      <div className={`text-[12px] mt-1 ${h.aligned ? "text-tc-bull" : "text-tc-bear"}`} data-testid="analyst-htf">
        {h.aligned ? "Timeframes are aligned." : "Timeframes conflict — treat directional ideas with more caution."}
      </div>
    </Card>
  );
}

function LevelsCard({ a, onExplain }) {
  return (
    <Card icon={Crosshair} title="Key Levels (ranked)" tone="text-tc-xp" chip={<ExplainChip topic="why do these levels matter?" onExplain={onExplain} />}>
      <div className="space-y-1" data-testid="analyst-levels">
        {a.key_levels.slice(0, 5).map((l, i) => (
          <div key={i} className="flex items-center justify-between text-[12px]">
            <span className={l.role === "resistance" ? "text-tc-bear" : l.role === "support" ? "text-tc-bull" : "text-tc-muted"}>
              {l.role.replace(/_/g, " ")} ${l.price.toLocaleString()}
            </span>
            <span className="font-mono text-[10px] text-tc-muted">{l.distance_pct.toFixed(2)}% · {l.touches}× · str {l.strength}</span>
          </div>
        ))}
        {!a.key_levels.length && <div className="text-tc-muted text-sm">No dominant levels near price.</div>}
      </div>
    </Card>
  );
}

function RiskGeoCard({ a, onExplain }) {
  const rg = a.risk_geometry;
  return (
    <Card icon={Gauge} title="Risk Geometry" tone={rg.room_ok ? "text-tc-bull" : "text-tc-bear"} chip={<ExplainChip topic="why does the available R:R matter here?" onExplain={onExplain} />}>
      {rg.available ? (
        <div className="space-y-1 text-[12px]" data-testid="analyst-riskgeo">
          <div className="flex justify-between"><span className="text-tc-muted">Available R:R</span><span className={`font-mono ${rg.room_ok ? "text-tc-bull" : "text-tc-bear"}`}>{rg.risk_reward}R</span></div>
          <div className="flex justify-between"><span className="text-tc-muted">Stop distance</span><span className="font-mono text-tc-sub">{rg.stop_atr} ATR</span></div>
          <div className="flex justify-between"><span className="text-tc-muted">Invalidation</span><span className="font-mono text-tc-sub">${rg.invalidation?.toLocaleString()}</span></div>
          <p className={`mt-1 ${rg.room_ok ? "text-tc-sub" : "text-tc-bear"}`}>{rg.note}</p>
        </div>
      ) : <div className="text-tc-muted text-sm">{rg.note}</div>}
    </Card>
  );
}

function EvidenceCard({ a, onExplain }) {
  return (
    <Card icon={TrendingUp} title="Supporting & Conflicting Evidence" tone="text-tc-txt">
      <div className="grid sm:grid-cols-2 gap-3" data-testid="analyst-evidence">
        <div>
          <div className="text-[10px] font-mono uppercase text-tc-bull mb-1">Supporting</div>
          {a.supporting_evidence.length ? a.supporting_evidence.map((e, i) => (
            <div key={i} className={`text-[12px] border-l-2 pl-2 py-0.5 ${KIND_STYLE[e.kind]}`}><span className="font-mono text-[9px] opacity-70">{e.kind}</span> · {e.text}</div>
          )) : <div className="text-tc-muted text-[12px]">No clean directional support.</div>}
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase text-tc-bear mb-1">Conflicting</div>
          {a.conflicting_evidence.length ? a.conflicting_evidence.map((e, i) => (
            <div key={i} className={`text-[12px] border-l-2 pl-2 py-0.5 ${KIND_STYLE[e.kind]}`}><span className="font-mono text-[9px] opacity-70">{e.kind}</span> · {e.text}</div>
          )) : <div className="text-tc-muted text-[12px]">No material conflicts detected.</div>}
        </div>
      </div>
    </Card>
  );
}

function ThesisCard({ a }) {
  const t = a.primary_thesis;
  const alt = a.alternative_theses?.[0];
  return (
    <Card icon={Target} title="Thesis" tone="text-tc-coach">
      <div data-testid="analyst-thesis">
        <div className="text-[10px] font-mono uppercase text-tc-coach">Primary</div>
        <div className="text-sm text-tc-txt font-medium">{t.direction_or_state}</div>
        <div className="text-[11px] text-tc-muted mt-0.5">Actionability: {t.current_actionability.replace(/_/g, " ")}</div>
        {alt && <>
          <div className="text-[10px] font-mono uppercase text-tc-xp mt-3 flex items-center gap-1"><Split className="w-3 h-3" /> Alternative</div>
          <div className="text-[12px] text-tc-sub">{alt.rationale}</div>
          <div className="text-[11px] text-tc-muted mt-0.5">Confirms if: {alt.what_would_confirm}</div>
        </>}
      </div>
    </Card>
  );
}

function ConditionsCard({ a }) {
  return (
    <Card icon={Zap} title="Conditions & Invalidation" tone="text-tc-bull">
      <div data-testid="analyst-conditions">
        <div className="text-[10px] font-mono uppercase text-tc-bull">Triggers</div>
        {a.conditions.length ? a.conditions.map((c, i) => (
          <div key={i} className="text-[12px] text-tc-sub mt-0.5">▸ {c.label}</div>
        )) : <div className="text-tc-muted text-[12px]">No actionable trigger — this is a wait/pass.</div>}
        {a.invalidation?.label && <>
          <div className="text-[10px] font-mono uppercase text-tc-bear mt-3 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Invalidation</div>
          <div className="text-[12px] text-tc-bear">{a.invalidation.label}</div>
        </>}
      </div>
    </Card>
  );
}

function InterpretationCard({ streaming, draft, interp, draftRef, onRetry }) {
  return (
    <div className="tc-card p-5 space-y-3 border-tc-mastery/25 bg-tc-mastery/[0.03]" data-testid="analyst-interpretation">
      {streaming && !interp && (
        <div>
          <div className="flex items-center gap-2 text-[12px] text-tc-mastery"><Loader2 className="w-4 h-4 animate-spin" /> TRADECRAFT AI is interpreting the evidence…</div>
          <div ref={draftRef} className="mt-2 max-h-40 overflow-auto text-[12px] text-tc-muted font-mono whitespace-pre-wrap opacity-70" data-testid="analyst-draft">{draft}</div>
          <div className="text-[10px] text-tc-muted mt-1">Live draft — not authoritative until validated.</div>
        </div>
      )}
      {interp?.limit_reached && <UpgradePrompt message={interp.reason} tier={interp.tier} />}
      {interp && !interp.available && !interp.limit_reached && (
        <div className="flex items-center gap-2 text-[12px] text-tc-sub rounded-lg bg-white/5 border border-white/10 px-3 py-2.5" data-testid="analyst-interp-unavailable">
          <WifiOff className="w-4 h-4 text-tc-muted shrink-0" />
          AI interpretation is temporarily unavailable. The deterministic read above is complete and fully usable.
          <button onClick={onRetry} className="ml-auto text-tc-coach text-[11px] underline">retry</button>
        </div>
      )}
      {interp && interp.available && (
        <div className="space-y-3 text-[12.5px]">
          <div className="flex items-center justify-between">
            <p className="text-tc-txt" data-testid="analyst-interp-synthesis">{interp.synthesis}</p>
            <span className="shrink-0 ml-2 text-[9px] font-mono px-2 py-0.5 rounded bg-tc-mastery/10 text-tc-mastery border border-tc-mastery/30">{interp.meta?.model || "claude-sonnet-4-6"}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <RefList title="Supporting" tone="text-tc-bull" items={interp.supporting} testid="analyst-interp-supporting" />
            <RefList title="Conflicting" tone="text-tc-bear" items={interp.conflicting} testid="analyst-interp-conflicting" />
          </div>
          {interp.ambiguity && <Block label="Uncertainty" tone="text-tc-xp" text={interp.ambiguity} testid="analyst-interp-ambiguity" />}
          {interp.alternatives?.length > 0 && <Block label="Alternative reading" tone="text-tc-coach" list={interp.alternatives} testid="analyst-interp-alt" />}
          {interp.thesis_challenge && <div className="rounded-lg bg-tc-bear/5 border border-tc-bear/20 px-3 py-2" data-testid="analyst-interp-challenge"><span className="text-[10px] font-mono uppercase text-tc-bear flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Challenge to the thesis</span><p className="text-tc-sub mt-1">{interp.thesis_challenge}</p></div>}
          {interp.overlooked?.length > 0 && <Block label="Possibly overlooked" tone="text-tc-muted" list={interp.overlooked} testid="analyst-interp-overlooked" />}
          {interp.what_would_change_view && <Block label="What would change this view" tone="text-tc-mastery" text={interp.what_would_change_view} testid="analyst-interp-change" />}
          {interp.questions?.length > 0 && <Block label="Ask yourself" tone="text-tc-mastery" list={interp.questions} prefix="?" testid="analyst-interp-questions" />}
          <p className="text-[10.5px] text-tc-muted pt-1 border-t border-white/5">This interpretation is not authoritative. It can under-weight context and be wrong — evaluating and challenging it is your job.{interp.has_unverified_claims ? " (Some unverified figures were filtered out.)" : ""}</p>
        </div>
      )}
    </div>
  );
}

function RefList({ title, tone, items, testid }) {
  return (
    <div data-testid={testid}>
      <span className={`text-[10px] font-mono uppercase ${tone}`}>{title}</span>
      {items?.length ? items.map((it, i) => (
        <div key={i} className="mt-1">
          <p className="text-tc-sub">• {it.point}</p>
          {it.references?.length > 0 && <div className="flex flex-wrap gap-1 mt-0.5">{it.references.map((r) => <span key={r} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-tc-coach/10 text-tc-coach border border-tc-coach/20">{r.replace(/_/g, " ")}</span>)}</div>}
        </div>
      )) : <p className="text-tc-muted mt-0.5">—</p>}
    </div>
  );
}

function Block({ label, tone, text, list, prefix = "•", testid }) {
  return (
    <div data-testid={testid}>
      <span className={`text-[10px] font-mono uppercase ${tone}`}>{label}</span>
      {text && <p className="text-tc-sub mt-0.5">{text}</p>}
      {list && list.map((x, i) => <p key={i} className="text-tc-sub mt-0.5">{prefix} {x}</p>)}
    </div>
  );
}

function ChallengeBar({ assessmentId }) {
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState(null);
  const [reason, setReason] = useState("");
  const [stance, setStance] = useState(null);

  const send = async (s) => {
    setStance(s); setBusy(true); setResp(null);
    try {
      const { data } = await api.post("/analyst/challenge", { assessment_id: assessmentId, stance: s, reason });
      setResp(data);
    } catch (e) { const d = aiLimitDetail(e); if (d) notifyAiLimit(d); else toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div className="tc-card p-5 border-tc-coach/25" data-testid="analyst-challenge">
      <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><MessageSquareWarning className="w-3.5 h-3.5" /> Your judgment · challenge the analysis</div>
      <p className="text-[12px] text-tc-muted mt-1">You retain judgment. Well-supported disagreement is exactly the oversight ANALYST is meant to receive.</p>
      <textarea data-testid="challenge-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
        placeholder="Optional: ground your view in specific evidence (e.g. 'the 4H conflict is under-weighted')…"
        className="w-full mt-2 bg-tc-panel border border-white/10 rounded-lg px-3 py-2 text-sm text-tc-sub" />
      <div className="flex flex-wrap gap-2 mt-2">
        {[["AGREE", Check], ["DISAGREE", X], ["MISSING", AlertTriangle], ["SHOW_ME_WHY", HelpCircle]].map(([s, Icon]) => (
          <button key={s} data-testid={`challenge-${s.toLowerCase()}`} disabled={busy} onClick={() => send(s)}
            className={`text-[12px] px-3 py-1.5 rounded-lg border flex items-center gap-1.5 transition-colors ${stance === s ? "border-tc-coach text-tc-coach bg-tc-coach/10" : "border-white/10 text-tc-sub hover:border-white/25"}`}>
            <Icon className="w-3.5 h-3.5" /> {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>
      {busy && <div className="mt-3 flex items-center gap-2 text-tc-muted text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Examining…</div>}
      {resp && (
        <div className="mt-3 space-y-2" data-testid="challenge-response">
          <div className={`text-[11px] font-mono ${resp.oversight.well_supported ? "text-tc-bull" : "text-tc-muted"}`}>{resp.oversight.note}</div>
          {resp.available && resp.response && <p className="text-sm text-tc-sub">{resp.response}</p>}
          {!resp.available && <p className="text-sm text-tc-muted">AI response temporarily unavailable — your challenge was still recorded.</p>}
        </div>
      )}
    </div>
  );
}
